import * as React from 'react'

import type { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { XIcon } from 'lucide-react'
import { observer } from 'mobx-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import { UnsavedChangesDialog } from '~/client/src/shared/components/UnsavedChangesDialog/UnsavedChangesDialog'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import PhotoMarkupStore from './PhotoMarkup.store'
import PhotoMarkupStage from './PhotoMarkupStage'
import { PhotoMarkupMainToolbar } from './PhotoMarkupToolbars'

interface IProps {
  imageUrl: string
  fileName: string
  isMobileEnv?: boolean
  originalFile?: File
  onSave(file: File): Promise<void>
  onClose(): void
}

const CONTENT_CLASS =
  'tw:flex tw:max-w-none tw:flex-col tw:gap-0 tw:overflow-hidden tw:bg-white tw:p-0 tw:ring-0 tw:sm:max-w-none'
const DESKTOP_CONTENT_CLASS = `${CONTENT_CLASS} tw:top-[calc(50%+10px)] tw:w-max tw:rounded-lg`
const MOBILE_CONTENT_CLASS = `${CONTENT_CLASS} tw:top-auto tw:right-0 tw:bottom-0 tw:left-0 tw:w-auto tw:translate-x-0 tw:translate-y-0 tw:rounded-none tw:rounded-t-[16px]`
const LOADING_CONTENT_CLASS =
  'tw:w-auto tw:bg-transparent tw:p-0 tw:shadow-none tw:ring-0'
const DESKTOP_MARGIN = { horizontal: 120, vertical: 140 }
const MOBILE_TOP_MARGIN = 56
const OVERLAY_CLASS =
  'tw:bg-black/50 tw:supports-backdrop-filter:backdrop-blur-none'

function PhotoMarkupDialog({
  imageUrl,
  fileName,
  isMobileEnv,
  originalFile,
  onSave,
  onClose,
}: IProps): JSX.Element {
  const { translator } = Localization
  const stageRef = React.useRef<HTMLDivElement>(null)
  const headerRef = React.useRef<HTMLDivElement>(null)
  const [footer, setFooter] = React.useState<HTMLDivElement | null>(null)
  const [store] = React.useState(
    () =>
      new PhotoMarkupStore({ fileName, isMobileEnv: !!isMobileEnv, onSave }),
  )
  const [isConfirmingClose, setIsConfirmingClose] = React.useState(false)

  React.useEffect(() => {
    store.mount()
    store.load(imageUrl)
    return store.dispose
  }, [store])

  React.useLayoutEffect(() => {
    if (!footer) return undefined
    const measure = () => {
      const chrome = headerRef.current.offsetHeight + footer.offsetHeight
      store.setAvailableStageSize(
        isMobileEnv
          ? {
              width: window.innerWidth,
              height: window.innerHeight - MOBILE_TOP_MARGIN - chrome,
            }
          : {
              width: window.innerWidth - DESKTOP_MARGIN.horizontal,
              height: window.innerHeight - DESKTOP_MARGIN.vertical - chrome,
            },
      )
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(headerRef.current)
    observer.observe(footer)
    window.addEventListener('resize', measure)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [store, isMobileEnv, footer])

  const save = async () => {
    setIsConfirmingClose(false)
    if (await store.save()) onClose()
  }

  const keepOriginal = async () => {
    setIsConfirmingClose(false)
    if (await store.saveOriginal(originalFile)) onClose()
  }

  const discardOriginal = async () => {
    setIsConfirmingClose(false)
    await store.saveOriginal(originalFile)
    onClose()
  }

  const discard = originalFile ? discardOriginal : onClose

  const done = () => (store.hasChanges ? save() : keepOriginal())

  const attemptClose = () => {
    if (store.isSaving) return
    if (store.hasChanges) setIsConfirmingClose(true)
    else discard()
  }

  const onOpenChange = (
    isOpen: boolean,
    eventDetails: DialogPrimitive.Root.ChangeEventDetails,
  ) => {
    if (isOpen) return
    if (store.isIdle) attemptClose()
    else eventDetails.allowPropagation()
  }

  const isLoading = !store.image && !store.hasLoadFailed

  const closeButton = (
    <Button
      variant="ghost"
      size="icon"
      aria-label={translator.close}
      disabled={store.isSaving}
      onClick={attemptClose}
      className="tw:text-muted-foreground"
    >
      <XIcon aria-hidden="true" />
    </Button>
  )

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        initialFocus={isLoading ? undefined : stageRef}
        overlayClassName={OVERLAY_CLASS}
        data-vaul-no-drag
        data-app-chrome={isMobileEnv ? 'surface' : undefined}
        className={
          isLoading
            ? LOADING_CONTENT_CLASS
            : isMobileEnv
              ? MOBILE_CONTENT_CLASS
              : DESKTOP_CONTENT_CLASS
        }
      >
        {isLoading ? (
          <>
            <DialogTitle className="tw:sr-only">
              {translator.markUpPhoto}
            </DialogTitle>
            <Spinner size="lg" />
          </>
        ) : (
          <>
            {isMobileEnv ? (
              <div
                ref={headerRef}
                className="tw:flex tw:items-center tw:justify-between tw:px-3"
              >
                {closeButton}
                <DialogTitle className="tw:truncate tw:text-sm tw:leading-5 tw:font-medium tw:tracking-tight tw:text-foreground">
                  {translator.markUpPhoto}
                </DialogTitle>
                <div className="tw:size-8" />
              </div>
            ) : (
              <div
                ref={headerRef}
                className="tw:flex tw:items-center tw:gap-2 tw:border-b tw:border-border tw:p-3"
              >
                {closeButton}
                <DialogTitle className="tw:truncate tw:text-base tw:leading-6 tw:font-semibold tw:tracking-[-0.176px] tw:text-foreground">
                  {translator.markUpPhoto}
                </DialogTitle>
              </div>
            )}
            <PhotoMarkupStage store={store} stageRef={stageRef} />
            <div
              ref={setFooter}
              className={
                isMobileEnv
                  ? 'tw:flex tw:flex-col tw:items-center tw:gap-3 tw:border-t tw:border-border tw:px-3 tw:pt-3 tw:pb-safe-3'
                  : 'tw:grid tw:grid-cols-[1fr_auto_1fr] tw:items-center tw:gap-3 tw:border-t tw:border-border tw:p-3'
              }
            >
              {!isMobileEnv && <div />}
              <div className={store.image ? undefined : 'tw:invisible'}>
                <PhotoMarkupMainToolbar store={store} />
              </div>
              <div className="tw:flex tw:items-center tw:justify-end tw:gap-2">
                {originalFile ? (
                  <Button
                    variant="default"
                    size="lg"
                    disabled={store.isSaving}
                    onClick={done}
                  >
                    {store.isSaving && <Spinner size="xs" variant="white" />}
                    {translator.done}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="lg"
                      disabled={store.isSaving}
                      onClick={attemptClose}
                    >
                      {translator.cancel}
                    </Button>
                    <Button
                      variant="default"
                      size="lg"
                      disabled={
                        !store.hasChanges || !store.image || store.isSaving
                      }
                      onClick={save}
                    >
                      {store.isSaving && <Spinner size="xs" variant="white" />}
                      {translator.save}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
      <UnsavedChangesDialog
        open={isConfirmingClose}
        title={translator.youHaveUnsavedChanges}
        onKeepEditing={() => setIsConfirmingClose(false)}
        onDiscard={discard}
        onSave={save}
      />
    </Dialog>
  )
}

export default observer(PhotoMarkupDialog)
